import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BellRing, CheckCircle2, History, Phone, ArrowRight, MailCheck, PhoneCall, Voicemail } from "lucide-react";
import { ContactHistoryDialog } from "@/components/admin/ContactHistoryDialog";

const ICON = { email: MailCheck, call: PhoneCall, voicemail: Voicemail };
const LABEL = { email: "Emailed", call: "Called", voicemail: "Left voicemail" };
const today = () => new Date().toLocaleDateString("en-CA");
const daysLate = (due) => Math.round((new Date(`${today()}T12:00:00`) - new Date(`${due}T12:00:00`)) / 86400000);
const short = (d) => new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const Row = ({ p, onHistory }) => {
  const late = daysLate(p.followUpDue);
  const lo = p.lastOutreach;
  const Icon = (lo && ICON[lo.channel]) || History;
  return (
    <li className="flex items-center gap-3 py-2.5" data-testid={`followup-row-${p.id}`}>
      <span className="w-8 h-8 rounded-full bg-amber-500/15 text-amber-300 flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">{p.name}</div>
        <div className="text-xs text-muted-foreground truncate">
          {lo ? `${LABEL[lo.channel] || lo.channel} ${short(lo.at)}${lo.template ? ` · ${lo.template}` : ""}` : `Last contacted ${p.lastContacted ? short(p.lastContacted) : "—"}`}
        </div>
        <span className={`sm:hidden text-[11px] font-bold ${late > 0 ? "text-rose-400" : "text-amber-400"}`}>{late > 0 ? `${late} day${late === 1 ? "" : "s"} overdue` : "Due today"}</span>
      </div>
      <span className={`hidden sm:inline text-[11px] font-bold whitespace-nowrap ${late > 0 ? "text-rose-400" : "text-amber-400"}`} data-testid={`followup-due-${p.id}`}>
        {late > 0 ? `${late} day${late === 1 ? "" : "s"} overdue` : "Due today"}
      </span>
      {p.phone && <a href={`tel:${p.phone}`} title={`Call ${p.phone}`} className="w-8 h-8 rounded-lg border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors" data-testid={`followup-call-${p.id}`}><Phone className="w-4 h-4" /></a>}
      <button type="button" onClick={() => onHistory(p)} title="Contact history" className="w-8 h-8 rounded-lg border border-border flex items-center justify-center hover:border-primary hover:text-primary transition-colors" data-testid={`followup-history-${p.id}`}><History className="w-4 h-4" /></button>
    </li>
  );
};

export const FollowUpsDueCard = ({ onOpenProspects }) => {
  const [items, setItems] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  useEffect(() => {
    api.get("/prospects", { params: { follow_up: "due", type: "Restaurant" } })
      .then(({ data }) => setItems((data.items || []).sort((a, b) => (a.followUpDue || "").localeCompare(b.followUpDue || ""))))
      .catch(() => setItems([]));
  }, []);
  if (items === null) return null;
  const shown = items.slice(0, 6);
  return (
    <div className="rounded-2xl border border-border bg-card p-5 sm:p-6" data-testid="followups-due-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${items.length ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}>
            {items.length ? <BellRing className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
          </span>
          <div>
            <h3 className="font-heading font-bold text-lg">Follow-ups due today</h3>
            <p className="text-xs text-muted-foreground" data-testid="followups-due-count">{items.length ? `${items.length} prospect${items.length === 1 ? "" : "s"} waiting to hear from you` : "You're all caught up — nothing due today."}</p>
          </div>
        </div>
        {items.length > 0 && <Button size="sm" variant="outline" onClick={onOpenProspects} data-testid="followups-open-prospects">View all<ArrowRight className="w-4 h-4 ml-1.5" /></Button>}
      </div>
      {shown.length > 0 && <ul className="mt-3 divide-y divide-border" data-testid="followups-list">{shown.map((p) => <Row key={p.id} p={p} onHistory={setHistoryFor} />)}</ul>}
      {items.length > shown.length && <p className="text-xs text-muted-foreground mt-2">+{items.length - shown.length} more in Prospects</p>}
      {!items.length && <p className="text-xs text-muted-foreground mt-3">When you mark a prospect as emailed, called or voicemailed, it shows up here on its follow-up date.</p>}
      <ContactHistoryDialog prospect={historyFor} onClose={() => setHistoryFor(null)} />
    </div>
  );
};
